import {
  Alert,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AuthForm from 'components/AuthForm/AuthForm';
import { Header } from 'components/Header/Header';
import EnrollmentListTable from 'components/Moderator/enrollsList/EnrollsList';
import { useCallback, useEffect, useState } from 'react';
import './styles.css';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import { ipayNet, newServer } from 'constants/constants';
import EnrollItem from 'components/Moderator/EnrollItem';
import { HomeConfig } from 'Config/home.config';

/**
 * Filtros del listado.
 *
 * `all` en un filtro significa "no enviar el parametro". Son los mismos que
 * acepta el backend (`provider_choice`, `origin`, `pending_info`).
 */
interface Filters {
  provider_choice: string;
  origin: string;
  pending_info: string;
}

const EMPTY_FILTERS: Filters = {
  provider_choice: 'all',
  origin: 'all',
  pending_info: 'all',
};

const Backoffice = () => {
  const { user, logout } = useLogin();
  const [currentEnrrol, setCurrentEnrrol] = useState();
  const [enrolls, setEnrolls] = useState<any>([]);
  const [requestInfo, setRequestInfo] = useState<any>();
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unauthorizedMessage, setUnauthorizedMessage] = useState<string>('');

  const fetchEnrollments = useCallback(
    async (targetPage: number = 1, activeFilters: Filters = EMPTY_FILTERS) => {
      if (Number(user.role_id) !== 9) {
        logout();
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(targetPage),
          limit: String(limit),
        });

        if (activeFilters.provider_choice !== 'all') {
          params.set('provider_choice', activeFilters.provider_choice);
        }
        if (activeFilters.origin !== 'all') {
          params.set('origin', activeFilters.origin);
        }
        if (activeFilters.pending_info !== 'all') {
          params.set('pending_info', activeFilters.pending_info);
        }

        /**
         * Se llama a `GET /api/requests` (sin id de status).
         *
         * Antes era `requests/1`, donde el `1` es un parametro de ruta que el
         * backend traduce a `status_dwolla_id = 1`. Eso escondia las solicitudes
         * de solo Usio y las diferidas, que no tienen ese status: el admin no
         * podia verlas ni aprobarlas.
         */
        const response = await fetch(`${newServer}requests?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user?.token}`,
            'x-api-key': ipayNet.apiToken,
          },
        });

        // `errorHandlerRoutes` responde texto plano en los errores.
        if (!response.ok) {
          const detail = await response.text();
          setError(detail || 'Could not load the requests.');
          setEnrolls([]);
          setRequestInfo([]);
          return;
        }

        const res = await response.json();

        if (res.message === 'Access denied') {
          logout();
          return;
        }
        if (res.message === 'JWT_EXPIRED') {
          logout();
          return;
        }

        setRequestInfo(res?.data);
        setEnrolls(
          res?.data?.map((item: any) => ({
            ...item.info_dwolla,
            id: item.id,
            ip_address: item.ip_address,
            location: item.location,
            browser: item.browser,
          }))
        );
        setTotalPages(Number(res?.total_pages) || 1);
      } catch (err: any) {
        setError(err?.message || 'Could not load the requests.');
      } finally {
        setLoading(false);
      }
    },
    [limit, logout, user?.role_id, user?.token]
  );

  useEffect(() => {
    if (user?.token) {
      if (Number(user.role_id) !== 9) {
        setUnauthorizedMessage(
          'Your account is not authorized to access the backoffice. Only super-admin users can sign in here. Please contact support if you need access.'
        );
        setTimeout(() => logout(), 4000);
        return;
      }
      fetchEnrollments(1, EMPTY_FILTERS);
    }
    // `user` cambia de identidad en cada render del provider: se depende del
    // token para no disparar el fetch en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  const paginateContent = (targetPage: number) => {
    if (targetPage < 1 || targetPage > totalPages) return;
    setPage(targetPage);
    fetchEnrollments(targetPage, filters);
  };

  /** Al cambiar un filtro se vuelve a la pagina 1: las paginas previas dejan de aplicar. */
  const updateFilter = (key: keyof Filters, value: string) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setPage(1);
    if (user?.token) fetchEnrollments(1, next);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    if (user?.token) fetchEnrollments(1, EMPTY_FILTERS);
  };

  const goBack = () => {
    setCurrentEnrrol(undefined);
    fetchEnrollments(page, filters);
  };

  /**
   * Propósito: Recarga la página actual del listado sin resetear filtros.
   */
  const refreshEnrollments = () => {
    if (!user?.token) {
      /**
       * Output:
       * Side effects — Sin sesión no se consulta
       */
      return;
    }
    /**
     * Output:
     * Side effects — Vuelve a pedir GET /requests con página y filtros actuales
     */
    fetchEnrollments(page, filters);
  };

  const filtersActive = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  return (
    /**
     * `bg-gray-100` no pintaba un gris: en `tailwind.config.js` los tonos
     * `gray` estaban redefinidos y esta clase salia NEGRO PURO, ademas de
     * ignorar el cambio de tema. `bg-background` es el token del fondo de
     * pagina y responde al modo claro/oscuro.
     */
    <Grid className='bg-background' style={{ minHeight: '100vh' }}>
      <Header />
      <Snackbar
        open={!!unauthorizedMessage}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setUnauthorizedMessage('')}
      >
        <Alert severity='error' variant='filled' onClose={() => setUnauthorizedMessage('')}>
          {unauthorizedMessage}
        </Alert>
      </Snackbar>
      {!user?.token && (
        <>
          <Typography variant='h4' align='center' className='auth-title'>
            Welcome to {HomeConfig.merchatName} Backoffice
          </Typography>
          <AuthForm />
        </>
      )}
      {user?.token && (
        <div>
          {!currentEnrrol && (
            // El navy anterior duplicaba el fondo de pagina con un color a mano.
            // El token `bg-background` ya lo define y sigue el tema.
            <div className='bg-background min-h-screen'>
              {/**
                * Filtros. Antes no existia ninguno y el listado solo mostraba las
                * solicitudes en "Waiting Internal Approval".
                */}
              <Card style={{ padding: '16px', margin: '16px' }}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} sm={4} md={3}>
                    <FormControl fullWidth size='small'>
                      <InputLabel id='filter-provider'>Payment provider</InputLabel>
                      <Select
                        labelId='filter-provider'
                        label='Payment provider'
                        value={filters.provider_choice}
                        onChange={(event) => updateFilter('provider_choice', event.target.value)}
                      >
                        <MenuItem value='all'>All providers</MenuItem>
                        <MenuItem value='usio'>Usio only</MenuItem>
                        <MenuItem value='dwolla'>Dwolla only</MenuItem>
                        <MenuItem value='both'>Usio + Dwolla</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4} md={3}>
                    <FormControl fullWidth size='small'>
                      <InputLabel id='filter-origin'>Origin</InputLabel>
                      <Select
                        labelId='filter-origin'
                        label='Origin'
                        value={filters.origin}
                        onChange={(event) => updateFilter('origin', event.target.value)}
                      >
                        <MenuItem value='all'>All origins</MenuItem>
                        <MenuItem value='landing'>Onboarding</MenuItem>
                        <MenuItem value='subdomain'>From subdomain</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4} md={3}>
                    <FormControl fullWidth size='small'>
                      <InputLabel id='filter-pending'>Payment status</InputLabel>
                      <Select
                        labelId='filter-pending'
                        label='Payment status'
                        value={filters.pending_info}
                        onChange={(event) => updateFilter('pending_info', event.target.value)}
                      >
                        <MenuItem value='all'>All</MenuItem>
                        {/* Estas son las que el admin tiene que gestionar. */}
                        <MenuItem value='true'>Pending setup</MenuItem>
                        <MenuItem value='false'>Ready to collect</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Button
                      variant='outlined'
                      onClick={clearFilters}
                      disabled={!filtersActive}
                      fullWidth
                    >
                      Clear filters
                    </Button>
                  </Grid>
                </Grid>
              </Card>

              {error && (
                <div style={{ margin: '16px' }}>
                  <Typography color='error' variant='body2'>
                    {error}
                  </Typography>
                </div>
              )}

                <EnrollmentListTable
                  data={enrolls}
                  requestInfo={requestInfo}
                  setCurrentEnrrol={(enrollment: any) => setCurrentEnrrol(enrollment)}
                  loading={loading}
                  onRefresh={refreshEnrollments}
                />

              <Stack
                component='nav'
                aria-label='Enrollment pages'
                direction='row'
                spacing={2}
                alignItems='center'
                justifyContent='center'
                sx={{
                  mx: 2,
                  mt: 1,
                  mb: 4,
                  py: 1.5,
                  px: 2,
                  flexWrap: 'wrap',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Button
                  variant='outlined'
                  startIcon={<ChevronLeftIcon />}
                  onClick={() => paginateContent(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </Button>
                <Typography variant='body2' color='text.secondary' sx={{ minWidth: 96, textAlign: 'center' }}>
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  variant='outlined'
                  endIcon={<ChevronRightIcon />}
                  onClick={() => paginateContent(page + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </Stack>
            </div>
          )}
          {currentEnrrol && (
            <EnrollItem
              data={currentEnrrol as any}
              /**
               * Se pasa la fila cruda ademas del `info_dwolla` aplanado, para que
               * el detalle pueda mostrar el proveedor, el origen y si ya puede
               * cobrar. Se busca por id porque la solicitud puede ser diferida y
               * entonces no hay correo con el que correlacionar.
               */
              request={(requestInfo || []).find(
                (item: any) => item.id === (currentEnrrol as any)?.id
              )}
              goBack={goBack}
            />
          )}
        </div>
      )}
    </Grid>
  );
};

export default Backoffice;
